import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Eye, EyeOff, Grid3X3, Group, History, Image as ImageIcon, Layers, Lock, LockOpen,
  MousePointer2, Redo2, Rocket, Save, Square, Trash2, Type, Undo2, Ungroup,
  ArrowUp, ArrowDown, Plus,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CANVAS_W = 1200;
const CANVAS_H = 800;
const GRID = 10;
const SNAP = 6;

const PAGES = [
  { id: "palais", label: "Palais Maasaï (/palais)" },
  { id: "univers", label: "Portail Univers (/)" },
  { id: "comptoir", label: "Le Comptoir (/comptoir)" },
  { id: "services", label: "Services (/services)" },
];

const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_PROPS = {
  text: { text: "Votre texte", fontSize: 28, color: "#F5F5F0", align: "left", bold: false },
  image: { url: "", radius: 0, opacity: 1 },
  button: { label: "Découvrir", href: "/", bg: "#C4704F", color: "#F5F5F0", radius: 999 },
  block: { bg: "#1a1a1a", radius: 8, opacity: 1, border: "#C6A87C" },
};

const newElement = (type, x = 80, y = 80) => ({
  id: uid(),
  type,
  x, y,
  w: type === "button" ? 220 : type === "block" ? 300 : 320,
  h: type === "button" ? 56 : type === "block" ? 200 : type === "image" ? 240 : 80,
  z: 1,
  locked: false,
  groupId: "",
  props: { ...DEFAULT_PROPS[type] },
});

export default function FreeCanvasEditor() {
  const [page, setPage] = useState("palais");
  const [elements, setElements] = useState([]);
  const [versions, setVersions] = useState([]);
  const [publishedCount, setPublishedCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [preview, setPreview] = useState(false);
  const [gridOn, setGridOn] = useState(true);
  const [guides, setGuides] = useState({ v: [], h: [] });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setSelected([]);
    api
      .get(`/admin/free-canvas/${page}`)
      .then((r) => {
        setElements(r.data.draft || []);
        setVersions(r.data.versions || []);
        setPublishedCount((r.data.published || []).length);
        setHistory([]);
        setFuture([]);
      })
      .catch(() => toast.error("Chargement du canvas impossible."))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const commit = (next) => {
    setHistory((h) => [...h.slice(-49), elements]);
    setFuture([]);
    setElements(next);
  };

  const undo = () => {
    if (!history.length) return;
    setFuture((f) => [elements, ...f]);
    setElements(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
  };

  const redo = () => {
    if (!future.length) return;
    setHistory((h) => [...h, elements]);
    setElements(future[0]);
    setFuture((f) => f.slice(1));
  };

  const scale = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return rect ? rect.width / CANVAS_W : 1;
  };

  const toCanvas = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const s = rect.width / CANVAS_W;
    return { x: (e.clientX - rect.left) / s, y: (e.clientY - rect.top) / s };
  };

  const snapValue = (value, candidates) => {
    const s = scale();
    for (const c of candidates) {
      if (Math.abs(value - c) <= SNAP / s) return { value: c, guide: c };
    }
    return { value, guide: null };
  };

  const computeSnap = (moving, others) => {
    // candidates verticaux (x) et horizontaux (y) depuis les autres éléments + centre canvas
    const xs = [0, CANVAS_W / 2, CANVAS_W];
    const ys = [0, CANVAS_H / 2, CANVAS_H];
    for (const o of others) {
      xs.push(o.x, o.x + o.w / 2, o.x + o.w);
      ys.push(o.y, o.y + o.h / 2, o.y + o.h);
    }
    const vGuides = [];
    const hGuides = [];
    let { x, y, w, h } = moving;
    // snap horizontal (x) : bords et centre de l'élément déplacé
    const xPoints = [
      { val: x, apply: (g) => { x = g; } },
      { val: x + w / 2, apply: (g) => { x = g - w / 2; } },
      { val: x + w, apply: (g) => { x = g - w; } },
    ];
    for (const p of xPoints) {
      const r = snapValue(p.val, xs);
      if (r.guide !== null) {
        p.apply(r.guide);
        vGuides.push(r.guide);
        break;
      }
    }
    const yPoints = [
      { val: y, apply: (g) => { y = g; } },
      { val: y + h / 2, apply: (g) => { y = g - h / 2; } },
      { val: y + h, apply: (g) => { y = g - h; } },
    ];
    for (const p of yPoints) {
      const r = snapValue(p.val, ys);
      if (r.guide !== null) {
        p.apply(r.guide);
        hGuides.push(r.guide);
        break;
      }
    }
    return { x, y, vGuides, hGuides };
  };

  const onPointerDown = (e, el, handle = null) => {
    if (preview || el.locked) return;
    e.stopPropagation();
    e.preventDefault();
    let sel = selected;
    if (e.shiftKey) {
      sel = selected.includes(el.id) ? selected.filter((i) => i !== el.id) : [...selected, el.id];
      setSelected(sel);
      if (handle) return;
    } else if (!selected.includes(el.id)) {
      sel = [el.id];
      setSelected(sel);
    }
    // déplace tout le groupe / toute la sélection
    const groupIds = new Set(elements.filter((x) => sel.includes(x.id) && x.groupId).map((x) => x.groupId));
    const movingIds = new Set(
      elements.filter((x) => sel.includes(x.id) || (x.groupId && groupIds.has(x.groupId))).filter((x) => !x.locked).map((x) => x.id)
    );
    const start = toCanvas(e);
    const origins = new Map(elements.map((x) => [x.id, { x: x.x, y: x.y, w: x.w, h: x.h }]));
    const beforeDrag = elements;
    dragRef.current = { id: el.id, handle, start, origins, movingIds, moved: false };

    const onMove = (ev) => {
      const drag = dragRef.current;
      if (!drag) return;
      const pos = toCanvas(ev);
      let dx = pos.x - drag.start.x;
      let dy = pos.y - drag.start.y;
      if (Math.abs(dx) + Math.abs(dy) > 1) drag.moved = true;
      if (gridOn && !drag.handle) {
        dx = Math.round(dx / GRID) * GRID;
        dy = Math.round(dy / GRID) * GRID;
      }
      setElements((prev) => {
        const others = prev.filter((x) => !drag.movingIds.has(x.id));
        const next = prev.map((x) => {
          if (!drag.movingIds.has(x.id)) return x;
          const o = drag.origins.get(x.id);
          if (drag.handle && x.id === drag.id) {
            // redimensionnement par poignée
            let { x: nx, y: ny, w: nw, h: nh } = o;
            if (drag.handle.includes("e")) nw = Math.max(30, o.w + dx);
            if (drag.handle.includes("s")) nh = Math.max(24, o.h + dy);
            if (drag.handle.includes("w")) { nw = Math.max(30, o.w - dx); nx = o.x + (o.w - nw); }
            if (drag.handle.includes("n")) { nh = Math.max(24, o.h - dy); ny = o.y + (o.h - nh); }
            return { ...x, x: nx, y: ny, w: nw, h: nh };
          }
          return { ...x, x: o.x + dx, y: o.y + dy };
        });
        // guides d'alignement sur l'élément principal
        if (!drag.handle) {
          const main = next.find((x) => x.id === drag.id);
          if (main) {
            const snapped = computeSnap(main, others);
            const sdx = snapped.x - main.x;
            const sdy = snapped.y - main.y;
            setGuides({ v: snapped.vGuides, h: snapped.hGuides });
            return next.map((x) => (drag.movingIds.has(x.id) ? { ...x, x: x.x + sdx, y: x.y + sdy } : x));
          }
        }
        return next;
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const wasMoved = dragRef.current?.moved;
      dragRef.current = null;
      setGuides({ v: [], h: [] });
      if (wasMoved) {
        setHistory((h) => [...h.slice(-49), beforeDrag]);
        setFuture([]);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const updateSelected = (patch) => {
    commit(elements.map((x) => (selected.includes(x.id) ? { ...x, ...patch } : x)));
  };

  const updateProps = (patch) => {
    commit(elements.map((x) => (selected.includes(x.id) ? { ...x, props: { ...x.props, ...patch } } : x)));
  };

  const addElement = (type) => {
    const el = newElement(type, 60 + elements.length * 16, 60 + elements.length * 16);
    el.z = Math.max(0, ...elements.map((x) => x.z || 0)) + 1;
    commit([...elements, el]);
    setSelected([el.id]);
  };

  const removeSelected = () => {
    commit(elements.filter((x) => !selected.includes(x.id)));
    setSelected([]);
  };

  const groupSelected = () => {
    if (selected.length < 2) return;
    const gid = uid();
    updateSelected({ groupId: gid });
    toast.success("Éléments groupés.");
  };

  const ungroupSelected = () => updateSelected({ groupId: "" });

  const reorder = (dir) => {
    const zs = elements.map((x) => x.z || 0);
    commit(
      elements.map((x) =>
        selected.includes(x.id)
          ? { ...x, z: dir === "up" ? Math.max(...zs) + 1 : Math.min(...zs) - 1 }
          : x
      )
    );
  };

  const toggleLock = () => {
    const anyUnlocked = elements.some((x) => selected.includes(x.id) && !x.locked);
    updateSelected({ locked: anyUnlocked });
    if (anyUnlocked) setSelected([]);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/free-canvas/${page}`, { elements });
      toast.success("Brouillon enregistré.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/free-canvas/${page}`, { elements });
      await api.post(`/admin/free-canvas/${page}/publish`);
      toast.success("Publié ! La mise en page est visible sur le site.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (vid) => {
    try {
      await api.post(`/admin/free-canvas/${page}/restore/${vid}`);
      toast.success("Version restaurée dans le brouillon.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      updateProps({ url: `${process.env.REACT_APP_BACKEND_URL}/api/media/${data.id}/file` });
      toast.success("Image envoyée dans la médiathèque.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sel = elements.filter((x) => selected.includes(x.id));
  const first = sel[0];
  const sorted = [...elements].sort((a, b) => (a.z || 0) - (b.z || 0));

  return (
    <div data-testid="free-canvas-editor">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Éditeur visuel libre</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ivory/40">
            Composez librement : déplacez, superposez, redimensionnez, groupez. Enregistrez un
            brouillon, prévisualisez, puis publiez. Rien n'est visible sur le site avant publication.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {PAGES.map((p) => (
            <button
              key={p.id}
              onClick={() => setPage(p.id)}
              className={`rounded-md border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${page === p.id ? "border-copper bg-copper text-ivory" : "border-white/10 text-ivory/60 hover:border-copper/50"}`}
              data-testid={`canvas-page-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="canvas-toolbar">
        <button onClick={() => addElement("text")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-add-text"><Type className="h-3.5 w-3.5" strokeWidth={1.5} />Texte</button>
        <button onClick={() => addElement("image")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-add-image"><ImageIcon className="h-3.5 w-3.5" strokeWidth={1.5} />Image</button>
        <button onClick={() => addElement("button")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-add-button"><MousePointer2 className="h-3.5 w-3.5" strokeWidth={1.5} />Bouton</button>
        <button onClick={() => addElement("block")} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-add-block"><Square className="h-3.5 w-3.5" strokeWidth={1.5} />Bloc</button>
        <span className="mx-1 h-6 w-px bg-white/10" />
        <button onClick={undo} disabled={!history.length} aria-label="Annuler" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-undo"><Undo2 className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={redo} disabled={!future.length} aria-label="Rétablir" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-redo"><Redo2 className="h-4 w-4" strokeWidth={1.5} /></button>
        <span className="mx-1 h-6 w-px bg-white/10" />
        <button onClick={() => setGridOn(!gridOn)} aria-label="Grille" className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${gridOn ? "border-gold text-gold" : "border-white/10 text-ivory/60"}`} data-testid="canvas-grid-toggle"><Grid3X3 className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={groupSelected} disabled={selected.length < 2} aria-label="Grouper" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-group"><Group className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={ungroupSelected} disabled={!sel.some((x) => x.groupId)} aria-label="Dissocier" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-ungroup"><Ungroup className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={toggleLock} disabled={!sel.length} aria-label="Verrouiller" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-lock">{sel.some((x) => !x.locked) ? <Lock className="h-4 w-4" strokeWidth={1.5} /> : <LockOpen className="h-4 w-4" strokeWidth={1.5} />}</button>
        <button onClick={() => reorder("up")} disabled={!sel.length} aria-label="Premier plan" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-front"><ArrowUp className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={() => reorder("down")} disabled={!sel.length} aria-label="Arrière-plan" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold disabled:opacity-30" data-testid="canvas-back"><ArrowDown className="h-4 w-4" strokeWidth={1.5} /></button>
        <button onClick={removeSelected} disabled={!sel.length} aria-label="Supprimer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-red-400 hover:text-red-400 disabled:opacity-30" data-testid="canvas-delete"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
        <span className="mx-1 h-6 w-px bg-white/10" />
        <button onClick={() => setPreview(!preview)} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-preview-toggle">{preview ? <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />}{preview ? "Quitter l'aperçu" : "Aperçu"}</button>
        <button onClick={saveDraft} disabled={saving} className="btn-outline !h-9 !px-3 !text-[11px]" data-testid="canvas-save"><Save className="h-3.5 w-3.5" strokeWidth={1.5} />Brouillon</button>
        <button onClick={publish} disabled={saving} className="btn-primary !h-9 !px-4 !text-[11px]" data-testid="canvas-publish"><Rocket className="h-3.5 w-3.5" strokeWidth={1.5} />Publier</button>
      </div>

      <p className="mb-3 text-[11px] text-ivory/35">
        {elements.length} élément(s) · {publishedCount} publié(s) · Maj+clic = multi-sélection · double-flèches aimantées et grille {gridOn ? "activées" : "désactivées"}
      </p>

      <div className="grid gap-6 xl:grid-cols-[1fr_300px]">
        <div className="overflow-hidden rounded-lg border border-white/10" data-testid="canvas-wrapper">
          {loading ? (
            <p className="p-10 text-sm text-ivory/40">Chargement…</p>
          ) : (
            <div
              ref={canvasRef}
              onPointerDown={() => !preview && setSelected([])}
              className="relative w-full touch-none overflow-hidden"
              style={{
                aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
                background: "#0a0a0a",
                backgroundImage: gridOn && !preview ? "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)" : "none",
                backgroundSize: `${GRID * scale()}px ${GRID * scale()}px`,
              }}
              data-testid="canvas-surface"
            >
              {sorted.map((el) => (
                <CanvasElement
                  key={el.id}
                  el={el}
                  selected={selected.includes(el.id)}
                  preview={preview}
                  scale={scale()}
                  onPointerDown={onPointerDown}
                />
              ))}
              {!preview && guides.v.map((x) => (
                <div key={`v${x}`} className="pointer-events-none absolute top-0 bottom-0 w-px bg-copper" style={{ left: `${(x / CANVAS_W) * 100}%` }} />
              ))}
              {!preview && guides.h.map((y) => (
                <div key={`h${y}`} className="pointer-events-none absolute left-0 right-0 h-px bg-copper" style={{ top: `${(y / CANVAS_H) * 100}%` }} />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {first && !preview && (
            <div className="space-y-4 rounded-lg border border-white/10 bg-coal/60 p-5" data-testid="canvas-props">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                <Layers className="h-3.5 w-3.5" strokeWidth={1.5} />
                {first.type}{sel.length > 1 ? ` (+${sel.length - 1})` : ""}
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {["x", "y", "w", "h"].map((k) => (
                  <div key={k}>
                    <label className="mb-1 block text-[10px] uppercase text-ivory/40">{k.toUpperCase()}</label>
                    <input
                      type="number"
                      value={Math.round(first[k])}
                      onChange={(e) => updateSelected({ [k]: Number(e.target.value) })}
                      className="input-dark !h-9 !px-2 !text-xs"
                      data-testid={`prop-${k}`}
                    />
                  </div>
                ))}
              </div>
              {first.type === "text" && (
                <>
                  <textarea rows={3} value={first.props.text} onChange={(e) => updateProps({ text: e.target.value })} className="input-dark !h-auto py-2 !text-xs" data-testid="prop-text" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase text-ivory/40">Taille</label>
                      <input type="number" value={first.props.fontSize} onChange={(e) => updateProps({ fontSize: Number(e.target.value) })} className="input-dark !h-9 !px-2 !text-xs" data-testid="prop-font-size" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase text-ivory/40">Couleur</label>
                      <input type="color" value={first.props.color} onChange={(e) => updateProps({ color: e.target.value })} className="h-9 w-full cursor-pointer rounded border border-white/10 bg-white/5" data-testid="prop-color" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {["left", "center", "right"].map((a) => (
                      <button key={a} onClick={() => updateProps({ align: a })} className={`flex-1 rounded border px-2 py-1.5 text-[10px] uppercase ${first.props.align === a ? "border-gold text-gold" : "border-white/10 text-ivory/50"}`} data-testid={`prop-align-${a}`}>{a === "left" ? "Gauche" : a === "center" ? "Centré" : "Droite"}</button>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-ivory/70">
                    <input type="checkbox" checked={!!first.props.bold} onChange={(e) => updateProps({ bold: e.target.checked })} className="accent-[#C4704F]" data-testid="prop-bold" />
                    Gras
                  </label>
                </>
              )}
              {first.type === "image" && (
                <>
                  <input value={first.props.url} onChange={(e) => updateProps({ url: e.target.value })} placeholder="URL de l'image" className="input-dark !h-9 !text-xs" data-testid="prop-image-url" />
                  <label className="btn-outline flex !h-9 cursor-pointer items-center justify-center gap-2 !text-[11px]" data-testid="prop-image-upload">
                    <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Téléverser
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
                  </label>
                </>
              )}
              {first.type === "button" && (
                <>
                  <input value={first.props.label} onChange={(e) => updateProps({ label: e.target.value })} placeholder="Texte du bouton" className="input-dark !h-9 !text-xs" data-testid="prop-button-label" />
                  <input value={first.props.href} onChange={(e) => updateProps({ href: e.target.value })} placeholder="Lien (ex : /reservation)" className="input-dark !h-9 !text-xs" data-testid="prop-button-href" />
                  <div>
                    <label className="mb-1 block text-[10px] uppercase text-ivory/40">Couleur de fond</label>
                    <input type="color" value={first.props.bg} onChange={(e) => updateProps({ bg: e.target.value })} className="h-9 w-full cursor-pointer rounded border border-white/10 bg-white/5" data-testid="prop-button-bg" />
                  </div>
                </>
              )}
              {first.type === "block" && (
                <div>
                  <label className="mb-1 block text-[10px] uppercase text-ivory/40">Couleur de fond</label>
                  <input type="color" value={first.props.bg} onChange={(e) => updateProps({ bg: e.target.value })} className="h-9 w-full cursor-pointer rounded border border-white/10 bg-white/5" data-testid="prop-block-bg" />
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg border border-white/10 bg-coal/60 p-5" data-testid="canvas-versions">
            <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              <History className="h-3.5 w-3.5" strokeWidth={1.5} />
              Versions publiées
            </h3>
            {versions.length === 0 ? (
              <p className="text-xs text-ivory/40" data-testid="versions-empty">Aucune version. Publiez pour créer un point de restauration.</p>
            ) : (
              <div className="space-y-2">
                {[...versions].reverse().map((v, i) => (
                  <div key={v.id} className="flex items-center justify-between rounded-md border border-white/10 px-3 py-2" data-testid={`version-${v.id}`}>
                    <span className="text-xs text-ivory/60">
                      v{versions.length - i} · {new Date(v.created_at).toLocaleString("fr-FR")} · {v.elements.length} él.
                    </span>
                    <button onClick={() => restore(v.id)} className="text-[11px] font-semibold uppercase tracking-wider text-copper hover:text-gold" data-testid={`version-restore-${v.id}`}>
                      Restaurer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function CanvasElement({ el, selected, preview, scale, onPointerDown }) {
  const style = {
    position: "absolute",
    left: `${(el.x / CANVAS_W) * 100}%`,
    top: `${(el.y / CANVAS_H) * 100}%`,
    width: `${(el.w / CANVAS_W) * 100}%`,
    height: `${(el.h / CANVAS_H) * 100}%`,
    zIndex: el.z || 1,
    cursor: preview ? "default" : el.locked ? "not-allowed" : "move",
    opacity: el.props.opacity ?? 1,
  };
  const p = el.props;
  return (
    <div
      style={style}
      onPointerDown={(e) => onPointerDown(e, el)}
      className={selected && !preview ? "outline outline-2 outline-copper" : ""}
      data-testid={`canvas-el-${el.id}`}
    >
      {el.type === "text" && (
        <div className="flex h-full w-full items-center overflow-hidden" style={{ justifyContent: p.align === "center" ? "center" : p.align === "right" ? "flex-end" : "flex-start" }}>
          <span style={{ fontSize: `${p.fontSize * scale}px`, color: p.color, fontWeight: p.bold ? 700 : 400, textAlign: p.align, lineHeight: 1.25 }}>{p.text}</span>
        </div>
      )}
      {el.type === "image" && (
        p.url ? (
          <img src={p.url} alt="" className="h-full w-full object-cover" style={{ borderRadius: p.radius }} draggable={false} />
        ) : (
          <div className="flex h-full w-full items-center justify-center border border-dashed border-white/20 text-[11px] text-ivory/30">Image</div>
        )
      )}
      {el.type === "button" && (
        <div className="flex h-full w-full items-center justify-center font-semibold uppercase tracking-wider" style={{ background: p.bg, color: p.color, borderRadius: p.radius, fontSize: `${13 * scale}px` }}>
          {p.label}
        </div>
      )}
      {el.type === "block" && (
        <div className="h-full w-full" style={{ background: p.bg, borderRadius: p.radius, border: `1px solid ${p.border || "transparent"}` }} />
      )}
      {el.locked && !preview && (
        <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-ivory/20">
          <Lock className="h-3 w-3 text-ivory" strokeWidth={2} />
        </span>
      )}
      {selected && !preview && !el.locked &&
        HANDLES.map((h) => <Handle key={h} name={h} onPointerDown={(e) => onPointerDown(e, el, h)} />)}
    </div>
  );
}

function Handle({ name, onPointerDown }) {
  const pos = {
    nw: "top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize",
    n: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize",
    ne: "top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize",
    e: "top-1/2 right-0 translate-x-1/2 -translate-y-1/2 cursor-ew-resize",
    se: "bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize",
    s: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize",
    sw: "bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize",
    w: "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize",
  };
  return (
    <span
      onPointerDown={onPointerDown}
      className={`absolute z-10 h-2.5 w-2.5 rounded-sm border border-ink bg-gold ${pos[name]}`}
      data-testid={`handle-${name}`}
    />
  );
}
